import React, { useContext, useEffect, useState } from 'react';
import CartContext from '../../../../contexts/CartContext';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ShippingClient from '../../../../api/ShippingClient';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Price from '../../shop/Price';
import { CartContents } from '../../../../contexts/CartContext';

const pickCartTenant = (cart: CartContents): string | undefined => {
  for (const item of Object.values(cart.items)) {
    if (item.tenant) return item.tenant;
  }
  return undefined;
};

const DeliveryAddress = () => {
  // Get the cart from the context
  const {cart} = useContext(CartContext);
  const [shipping, setShipping] = useState<ShippingDetails|undefined>();

  const subtotal = Object.values(cart.items).reduce(
    (sum, item) => sum + item.count * (item.item.price ?? 0),
    0,
  );
  const discountedSubtotal = cart.discountAmount
    ? subtotal - cart.discountAmount
    : subtotal;

  useEffect(() => {
    let cancelled = false;
    const doGetShipping = async() => {
      if (!cart.shippingMethod) {
        return;
      }
      const tenant = pickCartTenant(cart);
      try {
        const shippingDetails = await ShippingClient.getShipping(
          cart.shippingMethod, cart, discountedSubtotal, undefined, tenant,
        );
        if (!cancelled) setShipping(shippingDetails);
      } catch (err) {
        console.warn('Delivery shipping fetch failed', err);
        if (!cancelled) setShipping(undefined);
      }
    }

    doGetShipping();
    return () => {
      cancelled = true;
    };
  }, [cart.shippingMethod, cart.deliveryAddress, discountedSubtotal]);

  const minDays = shipping?.minDays ? shipping.minDays : 0;
  const maxDays = shipping?.maxDays ? shipping.maxDays : 0;

  // Apply the free-shipping threshold the same way the cart summary does.
  const usePrice = shipping?.freeShippingThreshold && discountedSubtotal >= shipping.freeShippingThreshold
    ? 0
    : shipping?.price;
  const hasPrice = usePrice !== undefined && usePrice >= 0;

  const getPickupText = () => {

    return (
      <Card className='delivery-description'>
        <CardContent>
          <div>
            Pick-up is available at The Atlantic Grow Shop, 186 Church Street, Liverpool Nova Scotia.
            A representative will contact you regarding a pickup time.
          </div>
        </CardContent>
      </Card>
    );
  }

  const getLocalText = () => {
    return (
      <Card className='delivery-description'>
        <CardContent>
          <div>
            Local delivery is only available within 40km of The Atlantic Grow Shop, 186 Church Street, Liverpool Nova Scotia at this time.
          </div>
        </CardContent>
      </Card>
    );
  }

  const getGenericText = () => {
    const hasDays = shipping?.minDays !== undefined && shipping?.maxDays !== undefined;
    return (
      <Card className='delivery-description'>
        <CardContent>
          <FormControl component="fieldset">
            <FormLabel component="legend">Delivery Estimate</FormLabel>
            <div>
              {hasDays
                ? `${minDays + 1}-${maxDays + 3} days`
                : 'Enter delivery address to see estimate'}
            </div>
            <div>
              {hasPrice
                ? <Price price={usePrice as number} showZero={true}/>
                : <span className='free-shipping'>Cost calculated once address is entered</span>}
            </div>
            {shipping?.freeShippingThreshold && usePrice !== 0 && (
              <div className='free-shipping'>
                (Free shipping at <Price price={shipping.freeShippingThreshold}/>)
              </div>
            )}
          </FormControl>
        </CardContent>
      </Card>
    );
  }

  switch (cart.shippingMethod) {
    case undefined:
    case null:
    case '':
      return null;
    case 'PICKUP':
      return getPickupText();
    case 'LOCAL':
      return getLocalText();
    // Everything else (STANDARD synthetic, ARMOUR, CP.*) renders the
    // generic estimate card.
    default:
      return getGenericText();
  }
}

export default DeliveryAddress
